import { useState } from 'react';
import { nativeShellVersion } from '../lib/native';

type Platform = 'ios' | 'android';

/** What the browser is running on, so the right steps come first. A desktop
 *  visitor still needs phone steps — the export only exists on the phone. */
function detect(): { platform: Platform; onPhone: boolean } {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return { platform: 'ios', onPhone: true };
  if (/Android/.test(ua)) return { platform: 'android', onPhone: true };
  // An iPad reporting as a Mac still has a touchscreen.
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return { platform: 'ios', onPhone: true };
  return { platform: /Mac/.test(ua) ? 'ios' : 'android', onPhone: false };
}

const STEPS: Record<Platform, Array<[string, string]>> = {
  ios: [
    ['Open the chat', 'In WhatsApp, open the conversation you want to keep.'],
    ['Tap the name at the top', 'The contact or group name, which opens its info screen.'],
    ['Scroll down to Export Chat', 'It is near the bottom, under the other options.'],
    ['Choose Without Media or Attach Media', 'Without media is far smaller and still contains every message.'],
    ['Tap Save to Files', 'Pick any folder you will remember.'],
    ['Come back here and choose that file', 'It is a .zip — the button above opens the file picker.'],
  ],
  android: [
    ['Open the chat', 'In WhatsApp, open the conversation you want to keep.'],
    ['Tap ⋮ then More then Export chat', 'The three dots are at the top right.'],
    ['Choose Without media or Include media', 'Without media is far smaller and still contains every message.'],
    ['Save it to your phone or Drive', 'Anywhere you can find it again.'],
    ['Come back here and choose that file', 'It is a .zip — the button above opens the file picker.'],
  ],
};

/**
 * The app used to assume you arrived holding a .zip. Nothing anywhere said how
 * to produce one, which is a strange thing to leave out of the only screen
 * that needs it.
 */
export function HowTo({ onClose }: { onClose: () => void }) {
  const found = detect();
  // Dentro do app da App Store não há Android nenhum a mostrar.
  //
  // A Apple rejeitou por isto — guideline 2.3.10, metadados exatos: um
  // seletor "iPhone / Android" numa app da App Store fala de uma plataforma
  // que não é a de quem está a ler. No site os dois fazem sentido, porque
  // quem chega de um telefone Android precisa dos passos dele. A casca
  // declara-se no User-Agent e isso é o que decide.
  const iosOnly = nativeShellVersion() > 0;
  const [platform, setPlatform] = useState<Platform>(iosOnly ? 'ios' : found.platform);

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label="How to export a chat">
      <div className="sheet-body">
        <header>
          <h2>Getting an export</h2>
          <button className="x" onClick={onClose} aria-label="Close">×</button>
        </header>

        <p className="fine">WhatsApp can hand you a whole conversation as a <code>.zip</code>.
           It is made on the phone — there is no way to do it from a computer.</p>

        {!iosOnly && (
          <div className="seg" role="tablist" aria-label="Phone type">
            {(['ios', 'android'] as Platform[]).map((p) => (
              <button key={p} role="tab" aria-selected={platform === p}
                      className={platform === p ? 'on' : ''}
                      onClick={() => setPlatform(p)}>
                {p === 'ios' ? 'iPhone' : 'Android'}
              </button>
            ))}
          </div>
        )}

        <ol className="steps">
          {STEPS[platform].map(([title, detail], i) => (
            <li key={i}>
              <span className="n">{i + 1}</span>
              <div><strong>{title}</strong><span className="fine">{detail}</span></div>
            </li>
          ))}
        </ol>

        {!found.onPhone && !iosOnly && (
          <p className="fine warn-soft">You are on a computer, so the export has to be made on
             your phone and brought across — AirDrop, Google Drive, or a cable. Or open this
             page on the phone itself and import it straight from there.</p>
        )}

        <h3>Worth knowing</h3>
        <ul className="plain">
          <li><strong>Without media</strong> keeps every message and is a few megabytes.
              With media it can run to several gigabytes.</li>
          <li>When you include media, WhatsApp limits how far back it reaches. Without
              media it goes much further.</li>
          <li>Nothing is read on our side. The file is decrypted and encrypted again on
              this device before any of it is uploaded.</li>
        </ul>
      </div>
    </div>
  );
}
